// "One request. Every <noun>." samples for FirstApiCall (homepage tabs and /[category] pages).
//
// Each entry lists one object for an API category. Endpoints, SDK method names
// (@unified-api/typescript-sdk `unified.<id>.<ts>`, Unified-python-sdk
// `unified.<id>.<py>` and its `res.<pyResult>` attribute) match the published SDK
// docs (docs/sdks/<category>/README.md in each SDK repo). Response fields and enum
// values come from https://api.unified.to/swagger.json. Keep the three in sync when
// adding a category.

export interface IApiParam {
    name: string; // query / Python request key
    ts: string; // TypeScript request key
    value: string;
}

export interface IApiSample {
    id: string; // API category, as in the request path
    label: string; // tab label
    noun: string; // "One request. Every <noun>."
    objects: string; // what the call returns, for the subtitle
    countLabel: string; // "...or any of 83 <countLabel> integrations"
    integrationCategory?: string; // integration-data category for the count, when it differs from id
    path: string; // object segment: /<id>/{connection_id}/<path>
    params?: IApiParam[]; // extra query params the call needs
    limit?: { name: string; ts: string } | false; // page-size param (default limit); false when the call returns one object
    variable: string; // TypeScript result variable
    ts: string;
    py: string;
    pyResult: string;
    examples?: string[]; // well-known integrations; otherwise the most popular in the data
    response: string;
}

const SAMPLES: IApiSample[] = [
    {
        id: 'crm',
        label: 'CRM',
        noun: 'CRM',
        objects: 'contacts',
        countLabel: 'CRM',
        path: 'contact',
        variable: 'contacts',
        ts: 'listCrmContacts',
        py: 'list_crm_contacts',
        pyResult: 'crm_contacts',
        examples: ['HubSpot', 'Salesforce', 'Pipedrive'],
        response: `[
  {
    "id": "5dc201f9446e439b204514a4",
    "created_at": "2026-09-14T16:44:22.170Z",
    "updated_at": "2026-10-21T09:12:05.000Z",
    "name": "Mary Jones",
    "first_name": "Mary",
    "last_name": "Jones",
    "title": "VP Operations",
    "company": "Foobar Inc.",
    "emails": [{ "email": "mary@foobar.com", "type": "WORK" }],
    "telephones": [{ "telephone": "+1-555-123-4567", "type": "WORK" }],
    "company_ids": ["5dc201f9446e439b09ab4143"],
    "deal_ids": []
  }
]`,
    },
    {
        id: 'ats',
        label: 'ATS',
        noun: 'ATS',
        objects: 'candidates',
        countLabel: 'ATS',
        path: 'candidate',
        variable: 'candidates',
        ts: 'listAtsCandidates',
        py: 'list_ats_candidates',
        pyResult: 'ats_candidates',
        examples: ['Greenhouse', 'Lever', 'Ashby'],
        response: `[
  {
    "id": "6a1f3c09d2e84b0012ab77c1",
    "created_at": "2026-10-02T13:05:41.000Z",
    "updated_at": "2026-10-20T17:48:12.000Z",
    "name": "Priya Shah",
    "first_name": "Priya",
    "last_name": "Shah",
    "title": "Senior Backend Engineer",
    "company_name": "Acme Robotics",
    "emails": [{ "email": "priya@example.com", "type": "HOME" }],
    "telephones": [{ "telephone": "+1-555-987-6543", "type": "MOBILE" }],
    "tags": ["referral"],
    "job_ids": ["6a1f2b88d2e84b0012ab6f20"]
  }
]`,
    },
    {
        id: 'hris',
        label: 'HRIS',
        noun: 'HRIS',
        objects: 'employees',
        countLabel: 'HRIS',
        path: 'employee',
        variable: 'employees',
        ts: 'listHrisEmployees',
        py: 'list_hris_employees',
        pyResult: 'hris_employees',
        examples: ['Workday', 'BambooHR', 'Rippling'],
        response: `[
  {
    "id": "5eb3081053162e8b59189272",
    "created_at": "2025-03-10T15:00:00.000Z",
    "updated_at": "2026-10-18T08:30:27.000Z",
    "name": "Daniel Kim",
    "first_name": "Daniel",
    "last_name": "Kim",
    "title": "Product Designer",
    "emails": [{ "email": "daniel.kim@foobar.com", "type": "WORK" }],
    "employment_status": "ACTIVE",
    "employment_type": "FULL_TIME",
    "hired_at": "2025-03-17T00:00:00.000Z",
    "manager_id": "5eb3081053162e8b59189101",
    "timezone": "America/Toronto"
  }
]`,
    },
    {
        id: 'messaging',
        label: 'Messaging',
        noun: 'messaging platform',
        objects: 'messages',
        countLabel: 'messaging',
        path: 'message',
        params: [{ name: 'channel_id', ts: 'channelId', value: '{channel_id}' }],
        variable: 'messages',
        ts: 'listMessagingMessages',
        py: 'list_messaging_messages',
        pyResult: 'messaging_messages',
        examples: ['Slack', 'Microsoft Teams', 'Discord'],
        response: `[
  {
    "id": "1729871542.004100",
    "created_at": "2026-10-25T15:52:22.000Z",
    "updated_at": "2026-10-25T15:52:22.000Z",
    "channels": [{ "id": "C04QK7Z8M2A", "name": "releases" }],
    "author_member": {
      "user_id": "U01ABCDE2FG",
      "name": "Alex Rivera",
      "email": "alex@foobar.com"
    },
    "message": "Release 2.4 is live in production.",
    "has_children": false,
    "web_url": "https://foobar.slack.com/archives/C04QK7Z8M2A"
  }
]`,
    },
    {
        id: 'calendar',
        label: 'Calendar',
        noun: 'calendar',
        objects: 'events',
        countLabel: 'calendar',
        path: 'event',
        variable: 'events',
        ts: 'listCalendarEvents',
        py: 'list_calendar_events',
        pyResult: 'calendar_events',
        examples: ['Google Calendar', 'Microsoft Outlook', 'Calendly'],
        response: `[
  {
    "id": "7g2kq1o9b0v8m3n4p5r6s7t8u9",
    "created_at": "2026-10-19T10:14:03.000Z",
    "updated_at": "2026-10-22T16:40:51.000Z",
    "calendar_id": "primary",
    "subject": "Quarterly business review",
    "start_at": "2026-10-28T14:00:00.000Z",
    "end_at": "2026-10-28T15:00:00.000Z",
    "timezone": "America/New_York",
    "is_all_day": false,
    "status": "CONFIRMED",
    "organizer": { "name": "Mary Jones", "email": "mary@foobar.com" },
    "attendees": [
      {
        "name": "Daniel Kim",
        "email": "daniel.kim@foobar.com",
        "status": "ACCEPTED"
      }
    ],
    "has_conference": true
  }
]`,
    },
    {
        id: 'accounting',
        label: 'Accounting',
        noun: 'accounting platform',
        objects: 'invoices',
        countLabel: 'accounting',
        path: 'invoice',
        variable: 'invoices',
        ts: 'listAccountingInvoices',
        py: 'list_accounting_invoices',
        pyResult: 'accounting_invoices',
        examples: ['QuickBooks', 'Xero', 'NetSuite'],
        response: `[
  {
    "id": "1042",
    "created_at": "2026-10-01T12:00:00.000Z",
    "updated_at": "2026-10-01T12:00:00.000Z",
    "invoice_number": "INV-1042",
    "contact_id": "58",
    "currency": "USD",
    "total_amount": 4800,
    "tax_amount": 300,
    "paid_amount": 0,
    "balance_amount": 4800,
    "due_at": "2026-10-31T00:00:00.000Z",
    "status": "AUTHORIZED",
    "lineitems": [
      {
        "item_name": "Annual subscription",
        "unit_amount": 4500,
        "unit_quantity": 1,
        "total_amount": 4500
      }
    ]
  }
]`,
    },
    {
        id: 'martech',
        label: 'Marketing',
        noun: 'marketing platform',
        objects: 'mailing lists',
        countLabel: 'marketing',
        path: 'list',
        variable: 'lists',
        ts: 'listMartechLists',
        py: 'list_martech_lists',
        pyResult: 'marketing_lists',
        examples: ['Mailchimp', 'HubSpot', 'Klaviyo'],
        response: `[
  {
    "id": "a1b2c3d4e5",
    "created_at": "2026-04-02T09:00:00.000Z",
    "updated_at": "2026-10-20T11:24:37.000Z",
    "name": "Product updates",
    "description": "Monthly product newsletter subscribers",
    "is_active": true,
    "sender_name": "Foobar Team",
    "sender_email": "news@foobar.com",
    "language": "en"
  }
]`,
    },
    {
        id: 'uc',
        label: 'Call Center',
        noun: 'call center platform',
        objects: 'calls',
        countLabel: 'call center',
        path: 'call',
        variable: 'calls',
        ts: 'listUcCalls',
        py: 'list_uc_calls',
        pyResult: 'uc_calls',
        examples: ['Dialpad', 'Zoom', 'Twilio'],
        response: `[
  {
    "id": "CA7f3e9b21c4d84e0f",
    "created_at": "2026-10-24T18:02:11.000Z",
    "updated_at": "2026-10-24T18:09:45.000Z",
    "type": "INBOUND",
    "status": "COMPLETED",
    "telephone": { "telephone": "+1-555-201-3344", "type": "MOBILE" },
    "contact_id": "5dc201f9446e439b204514a4",
    "user_name": "Alex Rivera",
    "start_at": "2026-10-24T18:02:11.000Z",
    "end_at": "2026-10-24T18:09:45.000Z",
    "duration_seconds": 454
  }
]`,
    },
    {
        id: 'enrich',
        label: 'Enrichment',
        noun: 'enrichment provider',
        objects: 'a person’s profile by email',
        countLabel: 'enrichment',
        path: 'person',
        params: [{ name: 'email', ts: 'email', value: 'jane@foobar.com' }],
        limit: false,
        variable: 'person',
        ts: 'listEnrichPeople',
        py: 'list_enrich_people',
        pyResult: 'enrich_person',
        examples: ['Apollo.io', 'Clearbit', 'ZoomInfo'],
        response: `{
  "name": "Jane Cooper",
  "first_name": "Jane",
  "last_name": "Cooper",
  "title": "Head of Partnerships",
  "company": "Foobar Inc.",
  "company_domain": "foobar.com",
  "emails": [
    { "email": "jane@foobar.com", "type": "WORK", "is_verified": true }
  ],
  "linkedin_url": "https://www.linkedin.com/in/janecooper",
  "timezone": "America/Chicago"
}`,
    },
    {
        id: 'storage',
        label: 'File Storage',
        noun: 'file storage provider',
        objects: 'files and folders',
        countLabel: 'file storage',
        path: 'file',
        variable: 'files',
        ts: 'listStorageFiles',
        py: 'list_storage_files',
        pyResult: 'storage_files',
        examples: ['Google Drive', 'Dropbox', 'Box'],
        response: `[
  {
    "id": "1Zx9kQ3mT7bV2nR8",
    "created_at": "2026-09-30T14:12:00.000Z",
    "updated_at": "2026-10-21T10:05:44.000Z",
    "name": "Q3 board deck.pdf",
    "type": "FILE",
    "mime_type": "application/pdf",
    "size": 2483117,
    "parent_id": "0AF3k9VxQmP1",
    "web_url": "https://drive.example.com/file/1Zx9kQ3mT7bV2nR8"
  }
]`,
    },
    {
        id: 'payment',
        label: 'Payments',
        noun: 'payment provider',
        objects: 'payments',
        countLabel: 'payments',
        path: 'payment',
        variable: 'payments',
        ts: 'listPaymentPayments',
        py: 'list_payment_payments',
        pyResult: 'payment_payments',
        examples: ['Stripe', 'Adyen', 'Braintree'],
        response: `[
  {
    "id": "pi_3QAx7b2eZvKYlo2C",
    "created_at": "2026-10-23T16:41:09.000Z",
    "updated_at": "2026-10-23T16:41:12.000Z",
    "total_amount": 129,
    "currency": "USD",
    "status": "SUCCEEDED",
    "tender_type": "CARD",
    "card_brand": "visa",
    "card_last4": "4242",
    "contact_id": "cus_R1kD2fGh3JkL",
    "paid_at": "2026-10-23T16:41:12.000Z"
  }
]`,
    },
    {
        id: 'commerce',
        label: 'E-Commerce',
        noun: 'e-commerce platform',
        objects: 'products',
        countLabel: 'e-commerce',
        path: 'item',
        variable: 'items',
        ts: 'listCommerceItems',
        py: 'list_commerce_items',
        pyResult: 'commerce_items',
        examples: ['Shopify', 'WooCommerce', 'BigCommerce'],
        response: `[
  {
    "id": "8123456789012",
    "created_at": "2026-06-12T10:00:00.000Z",
    "updated_at": "2026-10-22T07:31:18.000Z",
    "name": "Merino Crew Sweater",
    "slug": "merino-crew-sweater",
    "is_active": true,
    "vendor_name": "Foobar Apparel",
    "prices": [
      { "price": 89, "compare_at_price": 110, "currency": "USD" }
    ],
    "total_stock": 142,
    "requires_shipping": true
  }
]`,
    },
    {
        id: 'genai',
        label: 'Generative AI',
        noun: 'LLM provider',
        objects: 'models',
        countLabel: 'generative AI',
        path: 'model',
        variable: 'models',
        ts: 'listGenaiModels',
        py: 'list_genai_models',
        pyResult: 'genai_models',
        examples: ['OpenAI', 'Anthropic Claude', 'Google Gemini'],
        response: `[
  {
    "id": "example-large-2026-09",
    "name": "Example Large",
    "description": "Flagship model for complex reasoning and coding",
    "max_tokens": 200000,
    "has_vision": true,
    "has_temperature": true
  }
]`,
    },
    {
        id: 'kms',
        label: 'Knowledge Management',
        noun: 'knowledge base',
        objects: 'pages',
        countLabel: 'knowledge management',
        path: 'page',
        variable: 'pages',
        ts: 'listKmsPages',
        py: 'list_kms_pages',
        pyResult: 'kms_pages',
        examples: ['Atlassian Confluence', 'Notion', 'Guru'],
        response: `[
  {
    "id": "98304",
    "created_at": "2026-02-08T13:20:00.000Z",
    "updated_at": "2026-10-19T15:02:31.000Z",
    "title": "Incident response runbook",
    "type": "HTML",
    "space_id": "ENG",
    "is_active": true,
    "has_children": true,
    "web_url": "https://foobar.example.com/wiki/spaces/ENG/pages/98304"
  }
]`,
    },
    {
        id: 'task',
        label: 'Tasks',
        noun: 'task manager',
        objects: 'tasks',
        countLabel: 'task management',
        path: 'task',
        variable: 'tasks',
        ts: 'listTaskTasks',
        py: 'list_task_tasks',
        pyResult: 'task_tasks',
        examples: ['Asana', 'Atlassian Jira', 'Linear'],
        response: `[
  {
    "id": "1208741356032",
    "created_at": "2026-10-14T09:45:00.000Z",
    "updated_at": "2026-10-24T12:10:08.000Z",
    "name": "Ship onboarding checklist v2",
    "project_id": "1208741355981",
    "status": "IN_PROGRESS",
    "priority": "HIGH",
    "due_at": "2026-10-31T00:00:00.000Z",
    "assigned_user_ids": ["1199823412001"],
    "tags": ["onboarding"]
  }
]`,
    },
    {
        id: 'lms',
        label: 'LMS',
        noun: 'LMS',
        objects: 'courses',
        countLabel: 'learning management',
        path: 'course',
        variable: 'courses',
        ts: 'listLmsCourses',
        py: 'list_lms_courses',
        pyResult: 'lms_courses',
        examples: ['Docebo', 'TalentLMS', 'Cornerstone'],
        response: `[
  {
    "id": "c-20417",
    "created_at": "2026-01-20T08:00:00.000Z",
    "updated_at": "2026-10-11T14:22:50.000Z",
    "name": "Security awareness 2026",
    "description": "Annual security and phishing training for all staff",
    "is_active": true,
    "is_private": true,
    "duration_minutes": 45,
    "languages": ["en", "fr"]
  }
]`,
    },
    {
        id: 'repo',
        label: 'Code Repositories',
        noun: 'code host',
        objects: 'repositories',
        countLabel: 'code repository',
        path: 'repository',
        variable: 'repositories',
        ts: 'listRepoRepositories',
        py: 'list_repo_repositories',
        pyResult: 'repo_repositories',
        examples: ['GitHub', 'GitLab', 'BitBucket'],
        response: `[
  {
    "id": "784512903",
    "created_at": "2024-05-03T17:10:00.000Z",
    "updated_at": "2026-10-25T08:44:19.000Z",
    "name": "payments-service",
    "description": "Billing and payment processing service",
    "owner": "foobar",
    "is_private": true,
    "web_url": "https://github.com/foobar/payments-service"
  }
]`,
    },
    {
        id: 'shipping',
        label: 'Shipping',
        noun: 'shipping platform',
        objects: 'shipments',
        countLabel: 'shipping',
        path: 'shipment',
        variable: 'shipments',
        ts: 'listShippingShipments',
        py: 'list_shipping_shipments',
        pyResult: 'shipping_shipments',
        examples: ['ShipStation', 'Shippo', 'EasyPost'],
        response: `[
  {
    "id": "se-482910337",
    "created_at": "2026-10-23T19:05:00.000Z",
    "updated_at": "2026-10-24T22:18:41.000Z",
    "order_id": "1043",
    "status": "IN_TRANSIT",
    "carrier_name": "UPS",
    "tracking_id": "1Z999AA10123456784",
    "to_address": {
      "name": "Jane Cooper",
      "city": "Austin",
      "region_code": "TX",
      "country_code": "US"
    },
    "rate_amount": 12.4,
    "rate_currency": "USD"
  }
]`,
    },
    {
        id: 'forms',
        label: 'Forms',
        noun: 'form builder',
        objects: 'forms',
        countLabel: 'forms',
        path: 'form',
        variable: 'forms',
        ts: 'listFormsForms',
        py: 'list_forms_forms',
        pyResult: 'forms_forms',
        examples: ['Typeform', 'Google Forms', 'Jotform'],
        response: `[
  {
    "id": "fX7kq2Lm",
    "created_at": "2026-08-01T10:30:00.000Z",
    "updated_at": "2026-10-20T13:12:05.000Z",
    "name": "Customer onboarding survey",
    "is_active": true,
    "response_count": 318,
    "published_url": "https://forms.example.com/to/fX7kq2Lm"
  }
]`,
    },
    {
        id: 'assessment',
        label: 'Assessments',
        noun: 'assessment provider',
        objects: 'assessment packages',
        countLabel: 'assessment',
        path: 'package',
        variable: 'packages',
        ts: 'listAssessmentPackages',
        py: 'list_assessment_packages',
        pyResult: 'assessment_packages',
        examples: ['Greenhouse Assessment', 'Ashby Assessment', 'Workable Assessment'],
        response: `[
  {
    "id": "pkg_js_senior",
    "created_at": "2026-03-15T12:00:00.000Z",
    "updated_at": "2026-09-30T09:40:00.000Z",
    "name": "Senior JavaScript skills test",
    "type": "SKILLS_TEST",
    "description": "60-minute coding test for senior front-end roles",
    "max_score": 100
  }
]`,
    },
    {
        id: 'ads',
        label: 'Advertising',
        noun: 'ad platform',
        objects: 'campaigns',
        countLabel: 'advertising',
        path: 'campaign',
        variable: 'campaigns',
        ts: 'listAdsCampaigns',
        py: 'list_ads_campaigns',
        pyResult: 'ads_campaigns',
        examples: ['Google Ads', 'Meta Ads', 'Amazon Advertising'],
        response: `[
  {
    "id": "23851234567890",
    "created_at": "2026-10-01T15:00:00.000Z",
    "updated_at": "2026-10-24T06:30:12.000Z",
    "name": "Q4 retargeting",
    "status": "ACTIVE",
    "goal": "WEBSITE_TRAFFIC",
    "budget_amount": 150,
    "budget_period": "DAILY",
    "total_spend_amount": 3412.5,
    "currency": "USD",
    "start_at": "2026-10-01T00:00:00.000Z"
  }
]`,
    },
    {
        id: 'verification',
        label: 'Verification',
        noun: 'verification provider',
        objects: 'verification requests',
        countLabel: 'verification',
        path: 'request',
        variable: 'requests',
        ts: 'listVerificationRequests',
        py: 'list_verification_requests',
        pyResult: 'verification_requests',
        examples: ['Checkr', 'Certn', 'First Advantage'],
        response: `[
  {
    "id": "vr_8d2f61c0",
    "created_at": "2026-10-21T14:00:00.000Z",
    "updated_at": "2026-10-22T09:15:30.000Z",
    "package_id": "pkg_criminal_us",
    "candidate_id": "6a1f3c09d2e84b0012ab77c1",
    "profile_name": "Priya Shah",
    "response_status": "PASSED",
    "response_completed_at": "2026-10-22T09:15:30.000Z"
  }
]`,
    },
    {
        id: 'ticketing',
        label: 'Ticketing',
        noun: 'ticketing system',
        objects: 'tickets',
        countLabel: 'ticketing',
        path: 'ticket',
        variable: 'tickets',
        ts: 'listTicketingTickets',
        py: 'list_ticketing_tickets',
        pyResult: 'ticketing_tickets',
        examples: ['Zendesk', 'Intercom', 'Freshservice'],
        response: `[
  {
    "id": "48213",
    "created_at": "2026-10-25T11:02:44.000Z",
    "updated_at": "2026-10-25T11:40:09.000Z",
    "subject": "Can't export invoices to CSV",
    "status": "ACTIVE",
    "priority": "high",
    "customer_id": "39281",
    "source": "email",
    "tags": ["billing", "export"],
    "url": "https://foobar.example.com/tickets/48213"
  }
]`,
    },
    {
        id: 'signing',
        label: 'E-Signature',
        noun: 'e-signature platform',
        objects: 'documents',
        countLabel: 'e-signature',
        path: 'document',
        variable: 'documents',
        ts: 'listSigningDocuments',
        py: 'list_signing_documents',
        pyResult: 'signing_documents',
        examples: ['DocuSign', 'Adobe Sign', 'Dropbox Sign'],
        response: `[
  {
    "id": "3f9e2c71-8a4b-4d0e",
    "created_at": "2026-10-20T16:00:00.000Z",
    "updated_at": "2026-10-22T10:31:57.000Z",
    "name": "Master services agreement — Foobar Inc.",
    "status": "COMPLETED",
    "sent_at": "2026-10-20T16:05:00.000Z",
    "completed_at": "2026-10-22T10:31:57.000Z",
    "template_id": "tmpl_msa_v3"
  }
]`,
    },
    {
        id: 'clubs',
        label: 'Clubs',
        noun: 'club management platform',
        objects: 'members',
        countLabel: 'club management',
        path: 'member',
        variable: 'members',
        ts: 'listClubsMembers',
        py: 'list_clubs_members',
        pyResult: 'clubs_members',
        examples: ['Garmin', 'PlayHQ', 'Rev Sport'],
        response: `[
  {
    "id": "m-55120",
    "created_at": "2025-11-04T18:00:00.000Z",
    "updated_at": "2026-10-18T20:12:33.000Z",
    "name": "Sam Okafor",
    "first_name": "Sam",
    "last_name": "Okafor",
    "group_id": "g-301",
    "membership_status": "MEMBER",
    "is_admin": false
  }
]`,
    },
    {
        id: 'datastore',
        label: 'Datastore',
        noun: 'datastore',
        objects: 'table records',
        countLabel: 'datastore',
        path: 'record',
        params: [{ name: 'table_id', ts: 'tableId', value: '{table_id}' }],
        variable: 'records',
        ts: 'listDatastoreRecords',
        py: 'list_datastore_records',
        pyResult: 'datastore_records',
        examples: ['Airtable', 'Google Sheets', 'Snowflake'],
        response: `[
  {
    "id": "rec8Kx2mQ1pZ",
    "created_at": "2026-10-10T09:00:00.000Z",
    "updated_at": "2026-10-24T17:45:02.000Z",
    "table_id": "tblLeads",
    "row_number": 12,
    "fields": {
      "Company": { "string": "Foobar Inc." },
      "Seats": { "number": 40 },
      "Qualified": { "boolean": true }
    }
  }
]`,
    },
    {
        id: 'analytics',
        label: 'Analytics',
        noun: 'analytics platform',
        objects: 'events',
        countLabel: 'analytics',
        path: 'event',
        variable: 'events',
        ts: 'listAnalyticsEvents',
        py: 'list_analytics_events',
        pyResult: 'analytics_events',
        examples: ['Google Analytics', 'Mixpanel', 'Amplitude'],
        response: `[
  {
    "id": "evt_01JB3X8K2M",
    "created_at": "2026-10-25T14:31:07.000Z",
    "updated_at": "2026-10-25T14:31:07.000Z",
    "name": "pricing_viewed",
    "event_type": "PAGE_VIEW",
    "page_url": "https://foobar.com/pricing",
    "visitor_id": "v_8f21c4",
    "country_code": "CA",
    "utm_source": "newsletter"
  }
]`,
    },
    {
        id: 'cdp',
        label: 'CDP',
        noun: 'CDP',
        objects: 'customer profiles',
        countLabel: 'customer data platform',
        path: 'profile',
        variable: 'profiles',
        ts: 'listCdpProfiles',
        py: 'list_cdp_profiles',
        pyResult: 'cdp_profiles',
        examples: ['mParticle', 'Amperity', 'Adobe Experience Platform'],
        response: `[
  {
    "id": "prf_4c91e2",
    "created_at": "2026-05-19T12:00:00.000Z",
    "updated_at": "2026-10-24T21:08:55.000Z",
    "name": "Jane Cooper",
    "email": "jane@foobar.com",
    "company": "Foobar Inc.",
    "title": "Head of Partnerships",
    "is_anonymous": false
  }
]`,
    },
    {
        id: 'performance',
        label: 'Performance',
        noun: 'performance management platform',
        objects: 'reviews',
        countLabel: 'performance management',
        path: 'review',
        variable: 'reviews',
        ts: 'listPerformanceReviews',
        py: 'list_performance_reviews',
        pyResult: 'performance_reviews',
        examples: ['Lattice', '15Five', 'Culture Amp'],
        response: `[
  {
    "id": "rev_70214",
    "created_at": "2026-09-01T09:00:00.000Z",
    "updated_at": "2026-10-15T16:42:10.000Z",
    "user_id": "5eb3081053162e8b59189272",
    "reviewer_user_id": "5eb3081053162e8b59189101",
    "cycle_id": "cyc_2026_h2",
    "type": "MANAGER",
    "status": "SUBMITTED",
    "rating": 4,
    "rating_maximum": 5,
    "submitted_at": "2026-10-15T16:42:10.000Z"
  }
]`,
    },
    {
        id: 'social',
        label: 'Social Media',
        noun: 'social platform',
        objects: 'posts',
        countLabel: 'social media',
        path: 'post',
        variable: 'posts',
        ts: 'listSocialPosts',
        py: 'list_social_posts',
        pyResult: 'social_posts',
        examples: ['LinkedIn', 'Instagram', 'Facebook'],
        response: `[
  {
    "id": "7254018823001",
    "created_at": "2026-10-22T15:00:00.000Z",
    "updated_at": "2026-10-22T15:00:04.000Z",
    "profile_id": "foobar",
    "body": "Our new reporting dashboard is live. See what's new:",
    "type": "POST",
    "status": "PUBLISHED",
    "url": "https://social.example.com/foobar/posts/7254018823001"
  }
]`,
    },
    {
        id: 'legal',
        label: 'Legal',
        noun: 'practice management system',
        objects: 'matters',
        countLabel: 'legal practice management',
        path: 'matter',
        variable: 'matters',
        ts: 'listLegalMatters',
        py: 'list_legal_matters',
        pyResult: 'legal_matters',
        examples: ['Clio', 'Filevine', 'Actionstep'],
        response: `[
  {
    "id": "1100482",
    "created_at": "2026-07-08T13:30:00.000Z",
    "updated_at": "2026-10-23T18:05:12.000Z",
    "name": "Foobar Inc. — Trademark dispute",
    "number": "2026-0412",
    "status": "OPEN",
    "billing_method": "HOURLY",
    "is_billable": true,
    "currency": "USD",
    "opened_at": "2026-07-08T00:00:00.000Z"
  }
]`,
    },
    {
        id: 'compute',
        label: 'Compute',
        noun: 'GPU cloud',
        objects: 'instances',
        countLabel: 'compute',
        path: 'instance',
        variable: 'instances',
        ts: 'listComputeInstances',
        py: 'list_compute_instances',
        pyResult: 'compute_instances',
        examples: ['AWS EC2', 'Google Cloud Run', 'DigitalOcean'],
        response: `[
  {
    "id": "i-0a7f3c9e21b4",
    "created_at": "2026-10-24T08:00:00.000Z",
    "updated_at": "2026-10-25T02:11:40.000Z",
    "name": "finetune-run-14",
    "type": "VM",
    "status": "RUNNING",
    "market": "ON_DEMAND",
    "gpu_model": "NVIDIA_H100_SXM",
    "gpu_count": 8,
    "price_amount": 2.49,
    "price_unit": "PER_GPU_HOUR",
    "currency": "USD"
  }
]`,
    },
    {
        id: 'healthcare',
        label: 'Healthcare',
        noun: 'EHR',
        objects: 'appointments',
        countLabel: 'healthcare',
        path: 'appointment',
        variable: 'appts',
        ts: 'listHealthcareAppointments',
        py: 'list_healthcare_appointments',
        pyResult: 'healthcare_appointments',
        examples: ['Epic', 'athenahealth', 'Oracle Health'],
        response: `[
  {
    "id": "apt-88213",
    "created_at": "2026-10-20T15:12:00.000Z",
    "updated_at": "2026-10-20T15:12:00.000Z",
    "patient_id": "pat-40192",
    "practitioner_ids": ["prac-1173"],
    "location_id": "loc-12",
    "status": "BOOKED",
    "start_at": "2026-10-29T13:30:00.000Z",
    "end_at": "2026-10-29T14:00:00.000Z",
    "description": "Annual physical",
    "is_virtual": false
  }
]`,
    },
    {
        id: 'scim',
        label: 'SCIM',
        noun: 'HRIS',
        objects: 'employees as SCIM users',
        countLabel: 'HRIS',
        integrationCategory: 'hris',
        path: 'users',
        limit: { name: 'count', ts: 'count' },
        variable: 'users',
        ts: 'listScimUsers',
        py: 'list_scim_users',
        pyResult: 'scim_users',
        examples: ['Okta', 'Workday', 'BambooHR'],
        response: `[
  {
    "id": "5eb3081053162e8b59189272",
    "schemas": ["urn:ietf:params:scim:schemas:core:2.0:User"],
    "userName": "daniel.kim@foobar.com",
    "name": { "givenName": "Daniel", "familyName": "Kim" },
    "displayName": "Daniel Kim",
    "title": "Product Designer",
    "active": true,
    "emails": [
      {
        "value": "daniel.kim@foobar.com",
        "type": "work",
        "primary": true
      }
    ]
  }
]`,
    },
];

export const API_SAMPLES: Record<string, IApiSample> = Object.fromEntries(SAMPLES.map((s) => [s.id, s]));

/** The tabs on the homepage, in order. */
export const HOMEPAGE_SAMPLE_IDS = ['crm', 'ats', 'hris', 'messaging', 'calendar', 'accounting'];
